import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Loading from './icon/loading';
import { getApiBaseUrl } from '../config/api';
import { resolveAvatarUrl, handleAvatarError } from '../utils/profilePicUrl';
import { getAuthorDisplayName } from '../utils/displayName';
import { fetchBlockedUsers, unblockUser } from '../utils/moderation';
import './MyNetwork.css';

const SUGGESTION_PAGE = 12;

const TABS = [
  { id: 'connections', label: 'Connections' },
  { id: 'requests', label: 'Requests' },
  { id: 'following', label: 'Following' },
  { id: 'followers', label: 'Followers' },
  { id: 'suggestions', label: 'Suggestions' },
  { id: 'blocked', label: 'Blocked' },
];

function matchesNetworkQuery(person, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const name = getAuthorDisplayName(person).toLowerCase();
  const username = (person.username || '').toLowerCase();
  const country = (person.country || '').toLowerCase();
  const position = (person.position || '').toLowerCase();
  const company = (person.company || '').toLowerCase();
  const bio = (person.bio || '').toLowerCase();
  return (
    name.includes(q) ||
    username.includes(q) ||
    country.includes(q) ||
    position.includes(q) ||
    company.includes(q) ||
    bio.includes(q)
  );
}

function NetworkUserCard({
  person,
  API_URL,
  actions,
}) {
  const name = getAuthorDisplayName(person);
  const position = (person.position || '').trim();
  const company = (person.company || '').trim();
  const location = (person.country || '').trim();
  const metaLine = [company, location].filter(Boolean).join(' · ');
  return (
    <div className="mn-card">
      <Link to={`/profile/${person.id}`} className="mn-card-main">
        <img
          src={resolveAvatarUrl(person, API_URL)}
          alt=""
          className="mn-avatar"
          onError={handleAvatarError}
        />
        <div className="mn-card-info">
          <div className="mn-name">{name}</div>
          {person.username && <div className="mn-handle">@{person.username}</div>}
          {position && <div className="mn-position">{position}</div>}
          {metaLine && <div className="mn-meta">{metaLine}</div>}
          {person.bio && <p className="mn-bio">{person.bio}</p>}
        </div>
      </Link>
      {actions && <div className="mn-card-actions">{actions}</div>}
    </div>
  );
}

export default function MyNetwork({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabParam = searchParams.get('tab') || 'connections';
  const activeTab = TABS.some((t) => t.id === tabParam) ? tabParam : 'connections';

  const API_URL = getApiBaseUrl();
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [connections, setConnections] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [sugHasMore, setSugHasMore] = useState(false);
  const [sugLoading, setSugLoading] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const loadMoreRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const sugOffsetRef = useRef(0);

  const userId = user?.id || (() => {
    try {
      const stored = JSON.parse(localStorage.getItem('forumUser') || 'null');
      return stored?.id;
    } catch {
      return null;
    }
  })();

  const setTab = (id) => {
    setSearchParams({ tab: id });
    setQuery('');
    setDebouncedQuery('');
  };

  const filteredConnections = useMemo(
    () => connections.filter((p) => matchesNetworkQuery(p, query)),
    [connections, query]
  );
  const filteredIncoming = useMemo(
    () => incoming.filter((p) => matchesNetworkQuery(p, query)),
    [incoming, query]
  );
  const filteredOutgoing = useMemo(
    () => outgoing.filter((p) => matchesNetworkQuery(p, query)),
    [outgoing, query]
  );
  const filteredFollowing = useMemo(
    () => following.filter((p) => matchesNetworkQuery(p, query)),
    [following, query]
  );
  const filteredFollowers = useMemo(
    () => followers.filter((p) => matchesNetworkQuery(p, query)),
    [followers, query]
  );
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const hasQuery = query.trim().length > 0;
  const noMatchMessage = hasQuery
    ? `No matches for “${query.trim()}”.`
    : null;

  const postJson = async (endpoint, body) => {
    const res = await fetch(`${API_URL}/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  };

  const loadAll = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [connRes, reqRes, folRes, ferRes] = await Promise.all([
        fetch(`${API_URL}/get_connections.php?user_id=${userId}`).then((r) => r.json()),
        fetch(`${API_URL}/get_connection_requests.php?user_id=${userId}`).then((r) => r.json()),
        fetch(`${API_URL}/get_following.php?user_id=${userId}`).then((r) => r.json()),
        fetch(`${API_URL}/get_followers.php?user_id=${userId}`).then((r) => r.json()),
      ]);
      if (connRes.success) setConnections(connRes.connections || []);
      if (reqRes.success) {
        setIncoming(reqRes.incoming || []);
        setOutgoing(reqRes.outgoing || []);
      }
      if (folRes.success) setFollowing(folRes.following || []);
      if (ferRes.success) setFollowers(ferRes.followers || []);
    } catch (err) {
      console.error('Network load error:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, userId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const sugRequestRef = useRef(0);
  const fetchSuggestions = useCallback(async ({ append = false, search = '' } = {}) => {
    if (!userId) return;
    if (append && loadingMoreRef.current) return;
    const requestId = append ? sugRequestRef.current : sugRequestRef.current + 1;
    if (!append) sugRequestRef.current = requestId;
    loadingMoreRef.current = true;
    if (!append) setSugLoading(true);
    try {
      const offset = append ? sugOffsetRef.current : 0;
      const params = new URLSearchParams({
        user_id: String(userId),
        limit: String(SUGGESTION_PAGE),
        offset: String(offset),
      });
      if (search) params.set('q', search);
      const res = await fetch(`${API_URL}/get_network_suggestions.php?${params}`).then((r) => r.json());
      if (requestId !== sugRequestRef.current) return;
      if (res.success) {
        const batch = res.suggestions || [];
        setSuggestions((prev) => {
          if (!append) return batch;
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...batch.filter((p) => !seen.has(p.id))];
        });
        sugOffsetRef.current = offset + batch.length;
        setSugHasMore(Boolean(res.has_more));
      }
    } catch (err) {
      console.error('Suggestions load error:', err);
    } finally {
      if (requestId === sugRequestRef.current) {
        loadingMoreRef.current = false;
        setSugLoading(false);
      }
    }
  }, [API_URL, userId]);

  useEffect(() => {
    if (activeTab !== 'suggestions' || !userId) return;
    sugOffsetRef.current = 0;
    fetchSuggestions({ append: false, search: debouncedQuery });
  }, [activeTab, debouncedQuery, fetchSuggestions, userId]);

  useEffect(() => {
    if (activeTab !== 'suggestions') return undefined;
    const node = loadMoreRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          sugHasMore &&
          !loadingMoreRef.current &&
          !sugLoading
        ) {
          fetchSuggestions({ append: true, search: debouncedQuery });
        }
      },
      { root: null, rootMargin: '220px', threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [activeTab, sugHasMore, sugLoading, fetchSuggestions, debouncedQuery, suggestions.length]);

  const loadBlocked = useCallback(async () => {
    if (!userId) return;
    setBlocked(await fetchBlockedUsers(userId));
  }, [userId]);

  useEffect(() => {
    if (activeTab === 'blocked') loadBlocked();
  }, [activeTab, loadBlocked]);

  const withBusy = async (id, fn, { reload = true } = {}) => {
    setBusyId(id);
    try {
      await fn();
      if (reload) await loadAll();
    } finally {
      setBusyId(null);
    }
  };

  if (!userId) {
    return (
      <div className="mn-page">
        <p className="mn-empty">Please log in to view your network.</p>
      </div>
    );
  }

  return (
    <div className="mn-page">
      <header className="mn-header">
        <div>
          <h1>My Network</h1>
          <p>Grow your community — connect and follow learners on COOL.</p>
        </div>
        <button type="button" className="mn-back" onClick={() => navigate(-1)}>
          Back
        </button>
      </header>

      <nav className="mn-tabs" aria-label="Network sections">
        {TABS.map((t) => {
          let badge = null;
          if (t.id === 'requests' && incoming.length > 0) badge = incoming.length;
          if (t.id === 'connections') badge = connections.length;
          return (
            <button
              key={t.id}
              type="button"
              className={`mn-tab ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {badge != null && badge > 0 && <span className="mn-badge">{badge}</span>}
            </button>
          );
        })}
      </nav>

      <div className="mn-search">
        <i className="fa fa-search" aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, location, position, or company…"
          aria-label="Search people by name, location, position, or company"
        />
        {hasQuery && (
          <button
            type="button"
            className="mn-search-clear"
            onClick={() => setQuery('')}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {loading ? (
        <div className="mn-loading">
          <Loading />
        </div>
      ) : (
        <div className="mn-body">
          {activeTab === 'connections' && (
            <>
              {connections.length === 0 ? (
                <p className="mn-empty">No connections yet. Check Suggestions to find people.</p>
              ) : filteredConnections.length === 0 ? (
                <p className="mn-empty">{noMatchMessage}</p>
              ) : (
                <div className="mn-grid">
                  {filteredConnections.map((p) => (
                    <NetworkUserCard
                      key={p.id}
                      person={p}
                      API_URL={API_URL}
                      actions={
                        <button
                          type="button"
                          className="mn-btn mn-btn-ghost"
                          disabled={busyId === p.id}
                          onClick={() =>
                            withBusy(p.id, async () => {
                              await postJson('remove_connection.php', {
                                user_id: userId,
                                connection_id: p.connection_id,
                                target_id: p.id,
                              });
                            })
                          }
                        >
                          Remove
                        </button>
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'requests' && (
            <>
              <h2 className="mn-section-title">Incoming</h2>
              {incoming.length === 0 ? (
                <p className="mn-empty">No pending invitations.</p>
              ) : filteredIncoming.length === 0 ? (
                <p className="mn-empty">{noMatchMessage}</p>
              ) : (
                <div className="mn-grid">
                  {filteredIncoming.map((p) => (
                    <NetworkUserCard
                      key={p.id}
                      person={p}
                      API_URL={API_URL}
                      actions={
                        <>
                          <button
                            type="button"
                            className="mn-btn mn-btn-primary"
                            disabled={busyId === p.id}
                            onClick={() =>
                              withBusy(p.id, async () => {
                                await postJson('respond_connection.php', {
                                  user_id: userId,
                                  connection_id: p.connection_id,
                                  action: 'accept',
                                });
                              })
                            }
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            className="mn-btn mn-btn-ghost"
                            disabled={busyId === p.id}
                            onClick={() =>
                              withBusy(p.id, async () => {
                                await postJson('respond_connection.php', {
                                  user_id: userId,
                                  connection_id: p.connection_id,
                                  action: 'decline',
                                });
                              })
                            }
                          >
                            Decline
                          </button>
                        </>
                      }
                    />
                  ))}
                </div>
              )}

              <h2 className="mn-section-title">Outgoing</h2>
              {outgoing.length === 0 ? (
                <p className="mn-empty">No outgoing requests.</p>
              ) : filteredOutgoing.length === 0 ? (
                <p className="mn-empty">{noMatchMessage}</p>
              ) : (
                <div className="mn-grid">
                  {filteredOutgoing.map((p) => (
                    <NetworkUserCard
                      key={p.id}
                      person={p}
                      API_URL={API_URL}
                      actions={
                        <button
                          type="button"
                          className="mn-btn mn-btn-ghost"
                          disabled={busyId === p.id}
                          onClick={() =>
                            withBusy(p.id, async () => {
                              await postJson('withdraw_connection.php', {
                                user_id: userId,
                                connection_id: p.connection_id,
                                addressee_id: p.id,
                              });
                            })
                          }
                        >
                          Withdraw
                        </button>
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'following' && (
            <>
              {following.length === 0 ? (
                <p className="mn-empty">You are not following anyone yet.</p>
              ) : filteredFollowing.length === 0 ? (
                <p className="mn-empty">{noMatchMessage}</p>
              ) : (
                <div className="mn-grid">
                  {filteredFollowing.map((p) => (
                    <NetworkUserCard
                      key={p.id}
                      person={p}
                      API_URL={API_URL}
                      actions={
                        <button
                          type="button"
                          className="mn-btn mn-btn-ghost"
                          disabled={busyId === p.id}
                          onClick={() =>
                            withBusy(p.id, async () => {
                              await postJson('unfollow_user.php', {
                                follower_id: userId,
                                following_id: p.id,
                              });
                            })
                          }
                        >
                          Unfollow
                        </button>
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'followers' && (
            <>
              {followers.length === 0 ? (
                <p className="mn-empty">No followers yet.</p>
              ) : filteredFollowers.length === 0 ? (
                <p className="mn-empty">{noMatchMessage}</p>
              ) : (
                <div className="mn-grid">
                  {filteredFollowers.map((p) => (
                    <NetworkUserCard key={p.id} person={p} API_URL={API_URL} />
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'blocked' && (
            <>
              <p className="mn-hint">
                Blocked members can't see your posts or comments, message you, connect, or follow you — and you won't see theirs.
              </p>
              {blocked.length === 0 ? (
                <p className="mn-empty">You haven't blocked anyone.</p>
              ) : (
                <div className="mn-grid">
                  {blocked
                    .filter((p) => matchesNetworkQuery(p, query))
                    .map((p) => (
                      <NetworkUserCard
                        key={p.id}
                        person={p}
                        API_URL={API_URL}
                        actions={
                          <button
                            type="button"
                            className="mn-btn mn-btn-ghost"
                            disabled={busyId === p.id}
                            onClick={() =>
                              withBusy(p.id, async () => {
                                const result = await unblockUser(userId, p.id);
                                if (!result.success) {
                                  alert(result.message || 'Could not unblock.');
                                }
                                await loadBlocked();
                              }, { reload: false })
                            }
                          >
                            Unblock
                          </button>
                        }
                      />
                    ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'suggestions' && (
            <>
              {sugLoading && suggestions.length === 0 ? (
                <div className="mn-loading">
                  <Loading />
                </div>
              ) : suggestions.length === 0 ? (
                <p className="mn-empty">
                  {hasQuery
                    ? noMatchMessage
                    : 'No suggestions right now. Check back later.'}
                </p>
              ) : (
                <>
                  <div className="mn-grid">
                    {suggestions.map((p) => (
                      <NetworkUserCard
                        key={p.id}
                        person={p}
                        API_URL={API_URL}
                        actions={
                          <>
                            <button
                              type="button"
                              className="mn-btn mn-btn-primary"
                              disabled={busyId === p.id}
                              onClick={() =>
                                withBusy(
                                  p.id,
                                  async () => {
                                    await postJson('send_connection_request.php', {
                                      requester_id: userId,
                                      addressee_id: p.id,
                                    });
                                    setSuggestions((prev) => prev.filter((s) => s.id !== p.id));
                                  },
                                  { reload: false }
                                )
                              }
                            >
                              Connect
                            </button>
                            <button
                              type="button"
                              className="mn-btn mn-btn-ghost"
                              disabled={busyId === p.id}
                              onClick={() =>
                                withBusy(
                                  p.id,
                                  async () => {
                                    if (p.is_following) {
                                      await postJson('unfollow_user.php', {
                                        follower_id: userId,
                                        following_id: p.id,
                                      });
                                    } else {
                                      await postJson('follow_user.php', {
                                        follower_id: userId,
                                        following_id: p.id,
                                      });
                                    }
                                    setSuggestions((prev) =>
                                      prev.map((s) =>
                                        s.id === p.id
                                          ? { ...s, is_following: !s.is_following }
                                          : s
                                      )
                                    );
                                  },
                                  { reload: false }
                                )
                              }
                            >
                              {p.is_following ? 'Following' : 'Follow'}
                            </button>
                          </>
                        }
                      />
                    ))}
                  </div>
                  <div ref={loadMoreRef} className="mn-load-sentinel" aria-hidden="true" />
                  {sugLoading && suggestions.length > 0 && (
                    <p className="mn-loading-more">Loading more people…</p>
                  )}
                  {!sugHasMore && suggestions.length > 0 && (
                    <p className="mn-end">You’re all caught up — no more suggestions.</p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
